import { useCallback, useRef, useState } from "react";
import type { MessageCard, ShopApi } from "../../api/contract";
import type { ConversationController } from "../../hooks/useConversation";

import { useShopLoad } from "./useShopLoad";
import { shopRequest } from "./shopRequest";
import { ShopFieldsSkeleton, ShopLoadError } from "./ShopLoading";
import { Spinner } from "../Spinner";

type Revision = Awaited<ReturnType<ShopApi["getProductRevision"]>>;
type Card = Extract<MessageCard, { kind: "product-suggestion" }>;
const STATUS = { applied: "You applied this text. Undo is in the product's history.", kept: "You kept your own text.", changed: "The product changed, so this suggestion wasn't applied. Ask Stax again to use the latest version." };

/** Bodies are read from immutable Postgres revisions, never carried in the conversation. */
export function ProductSuggestionCard({ card, api, siteId, chat }: { card: Card; api: ShopApi; siteId: string; chat: ConversationController }) {
  const working = useRef(false);
  const read = useCallback(async (signal: AbortSignal): Promise<{ current: Revision; proposed: Revision }> => {
    const [current, proposed] = await Promise.all([api.getProductRevision(siteId, card.expectedRevision, signal), api.getProductRevision(siteId, card.revision, signal)]);
    return { current, proposed };
  }, [api, siteId, card.expectedRevision, card.revision]);
  const loaded = useShopLoad(read, `${siteId}/${card.expectedRevision}/${card.revision}`);
  const text = loaded.data;
  const [decisionFailed, setDecisionFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const choose = async (decision: "apply" | "keep") => {
    if (working.current) return; working.current = true;
    setBusy(true); setDecisionFailed(false);
    try { if (!await shopRequest(() => chat.decideProductSuggestion({ turnId: card.turnId, decision }))) setDecisionFailed(true); }
    catch { setDecisionFailed(true); }
    finally { working.current = false; setBusy(false); }
  };
  return <section className="bw-rewrite-card bw-product-suggestion" aria-label="Suggested product text" aria-busy={busy || loaded.busy}>
    <span className="bw-rewrite-label">Suggested product text</span>
    <strong>{card.name}</strong>
    {text ? <>
      {text.current.description !== text.proposed.description && <details open><summary>Description</summary><div className="bw-product-text-compare"><div><small>Current</small><p>{text.current.description || "No description"}</p></div><div><small>Suggested</small><p>{text.proposed.description || "No description"}</p></div></div></details>}
      {text.current.seo.title !== text.proposed.seo.title && <p><small>Search title</small><br />{text.proposed.seo.title || "No search title"}</p>}
      {text.current.seo.description !== text.proposed.seo.description && <p><small>Search description</small><br />{text.proposed.seo.description || "No search description"}</p>}
    </> : loaded.error ? <ShopLoadError message={loaded.error} retry={loaded.retry} /> : <ShopFieldsSkeleton label="Loading suggested product text" />}
    {card.decision ? <small>{STATUS[card.decision]}</small> : <>
      <div className="bw-actions"><button type="button" className="bw-btn bw-btn--primary" disabled={!text || busy || chat.connection !== "connected"} onClick={() => void choose("apply")}>{busy ? <><Spinner /> Saving…</> : "Apply"}</button><button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={() => void choose("keep")}>Keep mine</button></div>
      <small>Apply puts this text live straight away.</small>
      {decisionFailed && <p role="alert">Your choice wasn't confirmed. Try the same choice again.</p>}
    </>}
  </section>;
}
